import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';

import funnelI18n from '@app/providers/i18n';
import { Header } from '@widgets/header';
import { Footer } from '@widgets/footer';

/** Reset the scroll position to the top on every funnel step change, so each
 *  step (plan → confirm → account → …) opens from the top instead of inheriting
 *  the previous step's scroll offset. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Per-locale document title. Every locale used to get the same static
 * "Done-it · Subscription" (DIT-399).
 *
 * Set imperatively rather than through vite-react-ssg's `<Head>`: the funnel is
 * CSR-only and never prerendered, so there is no SSR head to hydrate, and `<Head>`
 * emitted nothing here in practice — the document ended up with no `<title>`
 * element at all. The marketing pages keep using `<Head>`, which is where it earns
 * its keep (per-page SEO baked at prerender).
 *
 * It reads from the FUNNEL i18next instance because it renders inside the provider
 * below, not against the mutable global default the marketing instances swap out.
 */
function FunnelTitle() {
  const { t, i18n } = useTranslation('common');
  const title = t('funnel.title');
  useEffect(() => {
    document.title = title;
  }, [title, i18n.resolvedLanguage]);
  return null;
}

/**
 * Shared shell for every route under /<lang>/... — Header + Outlet + Footer,
 * full-height column on the brand background. DS-styled (Tailwind + tokens).
 *
 * Pinned to the funnel i18next instance via `I18nextProvider`: reused marketing
 * DS components (Footer, pricing cards) spin up their own per-locale instances
 * whose `initReactI18next` swaps the *global* react-i18next default — so funnel
 * components must read their instance from context, not that mutable default,
 * or they'd fall back to raw keys.
 */
export default function Layout() {
  return (
    <I18nextProvider i18n={funnelI18n} defaultNS="common">
      <FunnelTitle />
      <ScrollToTop />
      <div className="flex min-h-screen flex-col bg-bg font-sans text-ink">
        <Header />
        <main className="flex flex-1 flex-col">
          <Outlet />
        </main>
        <Footer />
      </div>
    </I18nextProvider>
  );
}
